import React, { useState, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { contactInfo } from "../../mock";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, X, ExternalLink, MessageCircle } from "lucide-react";
import { t } from "../../i18n";
import { trackGoogleAdsConversion } from "../../utils/googleAds";

const ContactPage = () => {
    const { lang } = useLanguage();
    const [status, setStatus] = useState(null); // 'sending', 'success', 'error'
    const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
        if (showPopup) {
            const timer = setTimeout(() => {
                setShowPopup(false);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [showPopup]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setStatus("sending");

        const formData = new FormData(event.target);
        const endpoint = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
            ? "https://www.profinoks.com/send-mail.php"
            : "/send-mail.php";

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                setStatus("success");
                setShowPopup(true);
                trackGoogleAdsConversion('contact_form_submit', { form_type: 'contact_page' });
                event.target.reset();
            } else {
                setStatus("error");
                alert(data.message || t(lang, "contact.errorMsg") || "Error!");
            }
        } catch (error) {
            console.error("Submission error:", error);
            setStatus("error");
            alert(t(lang, "contact.errorMsg") || "Error!");
        }
    };

    return (
        <div className="pt-[115px] sm:pt-[125px] bg-background min-h-screen relative pb-16 text-foreground">
            {/* Success Popup */}
            {showPopup && (
                <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="bg-white border-l-4 border-green-500 shadow-2xl rounded-r-lg p-5 flex items-center space-x-4 min-w-[320px]">
                        <div className="bg-green-100 p-2 rounded-full">
                            <CheckCircle size={24} className="text-green-600" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                                {lang === "tr" ? "TEŞEKKÜRLER!" : "THANK YOU!"}
                            </h3>
                            <p className="text-xs text-gray-600 mt-1">
                                {t(lang, "contact.successMsg")}
                            </p>
                        </div>
                        <button 
                            onClick={() => setShowPopup(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>
            )}

            <div className="w-full max-w-[1900px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-12">
                {/* Heading */}
                <div className="mb-10 text-center">
                    <h1 className="text-3xl md:text-4xl font-semibold tracking-[0.2em] uppercase text-foreground mb-4">
                        {t(lang, "contact.heading")}
                    </h1>
                    <div className="h-1 w-20 bg-primary mx-auto"></div>
                </div>

                {/* Main Grid: Info + Form */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-12">
                    {/* Info Section - 5 Cols */}
                    <div className="lg:col-span-5 bg-white p-6 sm:p-8 rounded-2xl border border-black/10 shadow-sm space-y-8">
                        <div>
                            <h2 className="text-xl font-semibold tracking-wide text-foreground mb-6 border-l-4 border-primary pl-4 uppercase">
                                {t(lang, "contact.infoHeading")}
                            </h2>

                            <div className="space-y-6">
                                {/* ADRES */}
                                <div className="flex items-start">
                                    <div className="h-11 w-11 shrink-0 bg-primary/10 rounded-xl flex items-center justify-center mr-4 border border-primary/20 text-primary">
                                        <MapPin size={22} className="text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-widest text-primary font-bold mb-1">
                                            {t(lang, "contact.address")}
                                        </p>
                                        <p className="text-sm text-foreground/80 leading-relaxed font-medium">
                                            {contactInfo.address}
                                        </p>
                                    </div>
                                </div>

                                {/* TELEFON */}
                                <div className="flex items-start">
                                    <div className="h-11 w-11 shrink-0 bg-primary/10 rounded-xl flex items-center justify-center mr-4 border border-primary/20 text-primary">
                                        <Phone size={22} className="text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-widest text-primary font-bold mb-1">
                                            {t(lang, "contact.phone")}
                                        </p>
                                        <div className="space-y-1">
                                            {contactInfo.phone.map((p, idx) => (
                                                <p key={idx} className="text-sm text-foreground/80 font-medium">
                                                    <a 
                                                        href={`tel:${p.replace(/\s/g, "")}`} 
                                                        onClick={() => trackGoogleAdsConversion('phone_call_click', { source: 'contact_page', phone: p })}
                                                        className="hover:text-primary transition-colors"
                                                    >
                                                        {p}
                                                    </a>
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* WHATSAPP */}
                                <div className="flex items-start">
                                    <div className="h-11 w-11 shrink-0 bg-[#25D366]/10 rounded-xl flex items-center justify-center mr-4 border border-[#25D366]/20 text-[#25D366]">
                                        <MessageCircle size={22} className="text-[#25D366]" />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-widest text-[#25D366] font-bold mb-1">
                                            WHATSAPP
                                        </p>
                                        <div className="space-y-1">
                                            <p className="text-sm text-foreground/80 font-medium">
                                                <a 
                                                    href="https://api.whatsapp.com/send?phone=905306140705&text=Merhaba,%20fiyat%20teklifi%20almak%20istiyorum" 
                                                    target="_blank" 
                                                    rel="noreferrer"
                                                    onClick={() => trackGoogleAdsConversion('whatsapp_click', { source: 'contact_page' })}
                                                    className="hover:text-[#25D366] font-semibold transition-colors flex items-center"
                                                >
                                                    +90 530 614 07 05
                                                    <span className="ml-2 text-xs bg-[#25D366] text-white px-2 py-0.5 rounded-full font-bold">
                                                        {lang === "tr" ? "Hemen Yazın" : "Chat Now"}
                                                    </span>
                                                </a>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* E-POSTA */}
                                <div className="flex items-start">
                                    <div className="h-11 w-11 shrink-0 bg-primary/10 rounded-xl flex items-center justify-center mr-4 border border-primary/20 text-primary">
                                        <Mail size={22} className="text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-widest text-primary font-bold mb-1">
                                            {t(lang, "contact.email")}
                                        </p>
                                        <div className="space-y-1">
                                            {contactInfo.emails.map((e, idx) => (
                                                <p key={idx} className="text-sm text-foreground/80 font-medium">
                                                    <a href={`mailto:${e}`} className="hover:text-primary transition-colors">{e}</a>
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* ÇALIŞMA SAATLERİ */}
                                <div className="flex items-start">
                                    <div className="h-11 w-11 shrink-0 bg-primary/10 rounded-xl flex items-center justify-center mr-4 border border-primary/20 text-primary">
                                        <Clock size={22} className="text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-widest text-primary font-bold mb-1">
                                            {t(lang, "contact.hours")}
                                        </p>
                                        <p className="text-sm text-foreground/80 font-medium">
                                            {contactInfo[`workingHours_${lang}`]}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Section - 7 Cols */}
                    <div className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-2xl border border-black/10 shadow-sm relative overflow-hidden">
                        <h2 className="text-xl font-semibold tracking-wide text-foreground mb-6 border-l-4 border-primary pl-4 uppercase">
                            {t(lang, "contact.formHeading")}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-bold">{t(lang, "contact.nameLabel")}</label>
                                    <input 
                                        name="name"
                                        type="text" 
                                        required
                                        className="w-full bg-gray-50 border border-black/10 rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary transition-colors" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-bold">{t(lang, "contact.emailLabel")}</label>
                                    <input 
                                        name="email"
                                        type="email" 
                                        required
                                        className="w-full bg-gray-50 border border-black/10 rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary transition-colors" 
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-bold">{t(lang, "contact.subjectLabel")}</label>
                                <input 
                                    name="subject"
                                    type="text" 
                                    required
                                    className="w-full bg-gray-50 border border-black/10 rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary transition-colors" 
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-bold">{t(lang, "contact.messageLabel")}</label>
                                <textarea 
                                    name="message"
                                    rows="5" 
                                    required
                                    className="w-full bg-gray-50 border border-black/10 rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary transition-colors resize-y" 
                                ></textarea>
                            </div>
                            
                            <button 
                                type="submit" 
                                disabled={status === "sending"}
                                className="w-full bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-white text-xs font-bold uppercase tracking-[0.25em] py-4 rounded-xl transition-all flex items-center justify-center space-x-3 shadow-md hover:shadow-lg"
                            >
                                {status === "sending" ? (
                                    <>
                                        <div className="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                        <span>{lang === "tr" ? "GÖNDERİLİYOR..." : "SENDING..."}</span>
                                    </>
                                ) : (
                                    <>
                                        <Send size={16} />
                                        <span>{t(lang, "contact.sendButton")}</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Full-Width Google Map Section */}
                <div className="w-full bg-white p-4 sm:p-6 rounded-2xl border border-black/10 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-black/5">
                        <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0 border border-primary/20">
                                <MapPin size={20} />
                            </div>
                            <div>
                                <h3 className="text-base font-bold uppercase tracking-wider text-foreground">
                                    {lang === "tr" ? "FABRİKA & GENEL MERKEZ KONUMU" : "FACTORY & HEADQUARTERS LOCATION"}
                                </h3>
                                <p className="text-xs text-muted-foreground">
                                    {contactInfo.address}
                                </p>
                            </div>
                        </div>
                        <a
                            href="https://maps.app.goo.gl/a8pEtNFbV5qhqGAQ6"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-5 py-2.5 bg-primary text-white text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-primary/90 transition-colors shadow-sm self-start sm:self-auto"
                        >
                            <span>{lang === "tr" ? "Google Haritalar'da Aç" : "Open in Google Maps"}</span>
                            <ExternalLink size={14} />
                        </a>
                    </div>

                    <div className="h-[420px] md:h-[500px] w-full rounded-xl overflow-hidden border border-black/5">
                        <iframe
                            title="Profinoks Fabrika Konumu"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3011.089426917631!2d28.71501891541386!3d41.0005839793019!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14caa0e2d2a66bd5%3A0xfbba3e142f2fc92c!2sFiruzk%C3%B6y%2C%20Mezarl%C4%B1k%C3%BCst%C3%BC%20Cd.%20NO%20%3A13%2C%2034325%20Avc%C4%B1lar%2F%C4%B0stanbul!5e0!3m2!1str!2str!4v1725700000000!5m2!1str!2str"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;
